import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { imgFactionCar } from "@/shared/assets/img";
import { Search2Icon, UsersIcon } from "@/shared/icons";
import { TextInput } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import FactionTabsRow from "@/domains/faction/components/FactionTabsRow";
import FactionTransportCard from "@/domains/faction/components/FactionTransportCard";

type Props = {
  faction: FactionEnum;
};

export default function FactionTransportContainer({ faction }: Props) {
  const { t } = useTranslation("faction");

  const tabs = useMemo(() => ["all", "work", "special", "personal", "head"], []);

  const [inputValue, setInputValue] = useState("");
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const fakeData = useMemo(
    () => [
      {
        src: imgFactionCar,
        isBusy: true,
        title: "GNX",
        type: "work",
        name: "Александр Бомбео",
      },
      {
        src: imgFactionCar,
        isBusy: false,
        title: "BMW X5",
        type: "personal",
        name: "Иван Петров",
      },
      {
        src: imgFactionCar,
        isBusy: true,
        title: "Audi A6",
        type: "special",
        name: "Мария Смирнова",
      },
      {
        src: imgFactionCar,
        isBusy: false,
        title: "Lexus RX",
        type: "work",
        name: "Кирилл Соколов",
      },
      {
        src: imgFactionCar,
        isBusy: true,
        title: "Toyota Camry",
        type: "personal",
        name: "Елена Иванова",
      },
      {
        src: imgFactionCar,
        isBusy: false,
        title: "Mercedes E200",
        type: "special",
        name: "Николай Егоров",
      },
      {
        src: imgFactionCar,
        isBusy: true,
        title: "Mazda CX-5",
        type: "work",
        name: "Ольга Васильева",
      },
      {
        src: imgFactionCar,
        isBusy: false,
        title: "Kia Sportage",
        type: "personal",
        name: "Дмитрий Сидоров",
      },
      {
        src: imgFactionCar,
        isBusy: true,
        title: "Volkswagen Polo",
        type: "special",
        name: "Анна Кузнецова",
      },
      {
        src: imgFactionCar,
        isBusy: false,
        title: "Hyundai Tucson",
        type: "work",
        name: "Сергей Морозов",
      },
      {
        src: imgFactionCar,
        isBusy: true,
        title: "Ford Focus",
        type: "personal",
        name: "Татьяна Федорова",
      },
      {
        src: imgFactionCar,
        isBusy: false,
        title: "Chevrolet Cruze",
        type: "special",
        name: "Максим Павлов",
      },
    ],
    [],
  );

  const filteredData = useMemo(() => {
    const q = inputValue.trim().toLowerCase();

    const tabToType: Record<string, string | null> = {
      all: null,
      work: "work",
      special: "special",
      personal: "personal",
      head: "head",
    };
    const requiredType = tabToType[activeTab] ?? null;

    return fakeData.filter((item) => {
      const matchesTab = requiredType ? item.type === requiredType : true;
      const matchesQuery = q
        ? item.title.toLowerCase().includes(q) ||
          String(item.type).toLowerCase().includes(q) ||
          item.name.toLowerCase().includes(q)
        : true;
      return matchesTab && matchesQuery;
    });
  }, [activeTab, fakeData, inputValue]);

  return (
    <div className="flex flex-col h-full p-6">
      <FactionTabsRow
        title={t("faction.titles.transport")}
        Icon={UsersIcon}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        faction={faction}
        i18Path="faction.transportTabs"
      />
      <TextInput
        value={inputValue}
        onChange={setInputValue}
        maxLength={40}
        type="text"
        inputClassName="h-9.5 text-14"
        placeholder={t("faction.search")}
        icon={<Search2Icon className="size-4.5" />}
        className="mb-4"
      />
      <div className="relative grow inset-0">
        {filteredData.length > 0 ? (
          <div className="absolute top-0 left-0 bottom-0 grid grid-cols-5 auto-rows-min gap-[0.9375rem] pb-4 overflow-x-clip w-[calc(100%+0.9375rem)] pr-[0.6875rem] overflow-y-auto [--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242] [scrollbar-gutter:stable]">
            {filteredData.map((item) => (
              <FactionTransportCard
                key={`${item.title}-${item.name}`}
                src={item.src}
                title={item.title}
                type={item.type}
                name={item.name}
                isBusy={item.isBusy}
              />
            ))}
          </div>
        ) : (
          <div className="size-full flex flex-col items-center justify-center gap-5">
            <div className="text-16 font-bold text-white/50 leading-none uppercase">
              {t("faction.noTransport")}
            </div>
          </div>
        )}
      </div>
      <div className="relative pointer-events-none">
        <div className="absolute z-2 -top-14 left-0 w-full h-14 bg-gradient-to-t from-dark-900 to-transparent" />
        <div className="absolute z-2 -top-12 left-0 w-full h-12 bg-gradient-to-t from-dark-900 to-transparent" />
      </div>
    </div>
  );
}
